/**
 * Тонкая обёртка над `fetch` для MiniApp REST API (Stage 5.X.2).
 *
 * Возможности:
 *
 * 1. **Авто-добавление `Authorization: Bearer <access_token>`** из
 *    Zustand-store при каждом запросе (кроме явных публичных).
 * 2. **Авто-refresh при 401 `AUTH_TOKEN_EXPIRED`** — один раз пытается
 *    обновить access через refresh-токен, повторяет запрос. На fail —
 *    очищает store + бросает оригинальную ошибку (AuthGuard перенаправит
 *    на re-login через initData).
 * 3. **Concurrent refresh deduplication** — если 5 параллельных запросов
 *    одновременно получили 401, refresh выполняется ОДИН раз и шарится
 *    через `pending` promise. Иначе риск replay-detection backend'ом
 *    (см. `apps/miniapp_api/auth/use_case.py` rotation chain).
 * 4. **Typed `ApiError`** — JSON-envelope парсится в structured exception.
 *
 * Не использует axios — нативный `fetch` сокращает bundle на ~13KB.
 *
 * См. `docs/tg-miniapp.md` §8.0 (общие соглашения) + §10.2 (auth flow).
 */

import { ApiError, NetworkError } from '@/api/types';
import type { ErrorEnvelope, RefreshResponse } from '@/api/types';
import { useAuthStore } from '@/stores/auth';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Базовый префикс всех endpoint'ов MiniApp API (см. docs/tg-miniapp.md §8.0).
 *
 * В dev Vite-прокси (`vite.config.ts` `/api` → `localhost:8094`) делает
 * этот путь прозрачным. В prod nginx/Caddy маршрутизирует тот же путь.
 */
const API_PREFIX = '/api/v1/miniapp';

/**
 * Базовый URL для запросов (env override для тестов / staging).
 *
 * По умолчанию пусто — относительные пути работают и в Telegram WebView,
 * и в Vite-dev-server'е, и за reverse-proxy.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

// ---------------------------------------------------------------------------
// Concurrent refresh deduplication
// ---------------------------------------------------------------------------

/**
 * Промис активного refresh-запроса (или null если refresh не идёт).
 * Все параллельные 401-ответы await'ят его вместо своего refresh.
 */
let pendingRefresh: Promise<string> | null = null;

/**
 * Выполнить refresh access-токена (с дедупликацией).
 *
 * Returns: новый access_token. Бросает `ApiError` если refresh не прошёл
 * (caller обязан очистить store и редиректнуть на re-login).
 */
async function refreshAccessToken(): Promise<string> {
  if (pendingRefresh !== null) {
    return pendingRefresh;
  }

  const refreshTokenValue = useAuthStore.getState().refreshToken;
  if (refreshTokenValue === null) {
    throw new ApiError({
      code: 'AUTH_INVALID_REFRESH_TOKEN',
      message: 'No refresh token in store',
      status: 401,
    });
  }

  pendingRefresh = (async (): Promise<string> => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshTokenValue }),
      });

      if (!response.ok) {
        throw await parseApiError(response);
      }

      const data: RefreshResponse = await response.json();
      useAuthStore.getState().setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
      });
      return data.access_token;
    } finally {
      pendingRefresh = null;
    }
  })();

  return pendingRefresh;
}

// ---------------------------------------------------------------------------
// Error parsing
// ---------------------------------------------------------------------------

/**
 * Извлечь typed `ApiError` из 4xx/5xx response.
 *
 * Backend гарантирует envelope-форму `{ error: { code, message, ... } }`
 * (см. tg-miniapp.md §8.0.1). Если по какой-то причине пришло не-JSON —
 * fallback на generic `INTERNAL_ERROR`.
 */
async function parseApiError(response: Response): Promise<ApiError> {
  let envelope: ErrorEnvelope | null = null;
  try {
    envelope = (await response.json()) as ErrorEnvelope;
  } catch {
    // Non-JSON response — generic fallback.
  }

  if (envelope && envelope.error) {
    return new ApiError({
      code: envelope.error.code,
      message: envelope.error.message,
      status: response.status,
      details: envelope.error.details ?? null,
      traceId: envelope.error.trace_id ?? null,
    });
  }

  return new ApiError({
    code: 'INTERNAL_ERROR',
    message: `Unexpected response: HTTP ${response.status}`,
    status: response.status,
  });
}

// ---------------------------------------------------------------------------
// HTTP wrapper
// ---------------------------------------------------------------------------

export interface HttpOptions {
  /** HTTP-метод; default 'GET'. */
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';

  /** Тело запроса (JSON-сериализуется автоматически). */
  body?: unknown;

  /**
   * Дополнительные headers. `Content-Type` + `Authorization` добавляются
   * автоматически; явная установка переопределит.
   */
  headers?: Record<string, string>;

  /**
   * Skip-флаги:
   *
   * * `noAuth: true` — не добавлять `Authorization` header (для /auth/login).
   * * `noRetry: true` — не пытаться refresh при 401 (для /auth/refresh
   *   и /auth/logout — у них своя логика).
   */
  noAuth?: boolean;
  noRetry?: boolean;

  /** Сигнал отмены (для useEffect cleanup). */
  signal?: AbortSignal;
}

/**
 * Основная HTTP-функция MiniApp API.
 *
 * @param path относительный путь после `API_PREFIX` (например, `/profile`,
 *   `/auth/login`).
 * @param options см. HttpOptions.
 *
 * @returns parsed JSON ответа. Для 204 — `null`.
 *
 * @throws {ApiError} backend вернул 4xx/5xx с error-envelope.
 * @throws {NetworkError} fetch не дошёл до сервера (offline, CORS, ...).
 */
export async function http<T = unknown>(path: string, options: HttpOptions = {}): Promise<T> {
  return doRequest<T>(path, options, /*isRetry*/ false);
}

async function doRequest<T>(
  path: string,
  options: HttpOptions,
  isRetry: boolean,
): Promise<T> {
  const url = `${API_BASE_URL}${API_PREFIX}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  };

  if (!options.noAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    // AbortError перепрокидываем как есть (caller отличит через `.name`).
    if (cause instanceof Error && cause.name === 'AbortError') {
      throw cause;
    }
    throw new NetworkError(`Network error fetching ${path}`, cause);
  }

  if (response.ok) {
    if (response.status === 204) {
      return null as T;
    }
    return (await response.json()) as T;
  }

  const apiError = await parseApiError(response);

  // Авто-refresh + retry при 401 AUTH_TOKEN_EXPIRED (но не для самого refresh/logout).
  const isRetriable =
    !isRetry &&
    !options.noRetry &&
    response.status === 401 &&
    apiError.code === 'AUTH_TOKEN_EXPIRED' &&
    useAuthStore.getState().refreshToken !== null;

  if (isRetriable) {
    try {
      await refreshAccessToken();
    } catch {
      // Refresh не прошёл — store очищается caller'ом (AuthGuard вызовет re-login).
      useAuthStore.getState().clear();
      throw apiError; // оригинальная 401, чтобы UI понял "auth lost".
    }
    return doRequest<T>(path, options, /*isRetry*/ true);
  }

  throw apiError;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { API_PREFIX };
